import { create } from 'zustand'

import { toast } from '@/shared/lib/toast'

import * as leadsApi from '../api/leads.api'

const initialState = {
  leads: [],
  meta: null,
  isLoading: true,
  isSubmitting: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    search: '',
    source: 'all',
  },
}

export const useLeadsStore = create((set, get) => ({
  ...initialState,

  clearError: () => set({ error: null }),

  setFilters: (partial) =>
    set((state) => ({
      filters: {
        ...state.filters,
        ...partial,
      },
    })),

  fetchLeads: async (params = {}) => {
    const filters = {
      ...get().filters,
      ...params,
    }

    set((state) => ({
      isLoading: state.leads.length === 0,
      error: null,
      filters,
    }))

    try {
      const query = {
        page: filters.page,
        limit: filters.limit,
        search: filters.search || undefined,
        source: filters.source === 'all' ? undefined : filters.source,
        status: filters.status === 'all' ? undefined : filters.status,
        leadWarmth:
          filters.leadWarmth === 'all' ? undefined : filters.leadWarmth,
        period: filters.period === 'all' ? undefined : filters.period,
        startDate:
          filters.period === 'custom'
            ? filters.startDate || undefined
            : undefined,
        endDate:
          filters.period === 'custom'
            ? filters.endDate || undefined
            : undefined,
      }

      const result = await leadsApi.getLeads(query)

      set({
        leads: result.data ?? [],
        meta: result.meta,
        isLoading: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to fetch leads'

      set({
        isLoading: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  createLead: async (payload) => {
    if (get().isSubmitting) return null

    set({ isSubmitting: true, error: null })

    try {
      const result = await leadsApi.createLead(payload)
      toast.success(result.message || 'Lead created successfully')

      await get().fetchLeads({ page: 1 })

      set({ isSubmitting: false, error: null })
      return result
    } catch (error) {
      const message = error.message || 'Failed to create lead'

      set({ isSubmitting: false, error: message })
      toast.error(message)
      throw error
    }
  },

  updateLead: async (id, payload) => {
    set({ isSubmitting: true, error: null })

    try {
      const result = await leadsApi.updateLead(id, payload)
      toast.success(result.message || 'Lead updated successfully')

      await get().fetchLeads()

      set({
        isSubmitting: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to update lead'

      set({ isSubmitting: false, error: message })
      toast.error(message)
      throw error
    }
  },

  deleteLead: async (id) => {
    set({ isSubmitting: true, error: null })

    try {
      const result = await leadsApi.deleteLead(id)
      toast.success(result.message || 'Lead deleted successfully')

      await get().fetchLeads()

      set({
        isSubmitting: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to delete lead'

      set({ isSubmitting: false, error: message })
      toast.error(message)
      throw error
    }
  },

  bulkUploadLeads: async (file) => {
    set({ isSubmitting: true, error: null })

    const formatBulkErrors = (rawErrors = []) => {
      if (!Array.isArray(rawErrors) || rawErrors.length === 0) return ''

      const lines = rawErrors.slice(0, 8).map((item) => {
        if (typeof item !== 'object' || item == null) {
          return `• ${String(item)}`
        }

        const row = item.row ? `Row ${item.row}` : 'Row ?'
        const field = item.field ? ` · ${item.field}` : ''
        return `• ${row}${field}: ${item.message || 'Invalid data'}`
      })

      const remaining =
        rawErrors.length > 8
          ? `\n...and ${rawErrors.length - 8} more error(s)`
          : ''

      return `${lines.join('\n')}${remaining}`
    }

    try {
      const result = await leadsApi.bulkUploadLeads(file)
      const data = result.data || {}
      const rowErrors = data.errors || []

      if (data.skippedCount > 0) {
        const details = formatBulkErrors(rowErrors)
        toast.warning(
          `${data.createdCount ?? 0} lead(s) imported, ${data.skippedCount} row(s) skipped.`,
          {
            description: details || undefined,
            duration: 9000,
          },
        )
      } else {
        toast.success(result.message || 'Leads imported successfully')
      }

      await get().fetchLeads({ page: 1 })

      set({ isSubmitting: false, error: null })
      return result
    } catch (error) {
      const mainMsg = error.message || 'Failed to import leads'
      const rawErrors =
        error.data?.data ||
        error.data?.errors ||
        error.response?.data?.data ||
        []

      const details = formatBulkErrors(rawErrors)
      const formattedMessage = details ? `${mainMsg}\n${details}` : mainMsg

      set({ isSubmitting: false, error: formattedMessage })
      toast.error(mainMsg, {
        description: details || undefined,
        duration: 9000,
      })
      throw error
    }
  },
}))
